/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Fragment } from 'react'

import type { EuiBasicTableColumn, EuiTableActionsColumnType } from '@elastic/eui'
import { EuiFlexGroup, EuiFlexItem, EuiInMemoryTable, EuiSpacer } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type { NormalizedTrafficFilterInfo, RulesetType } from '@modules/network-security-lib/types'
import type { DeploymentGetResponse } from '@modules/cloud-api/v1/types'
import type { AnyUserProject } from '@modules/project-user-api/types'

import { nameColumn, providerColumn, regionColumn, typeColumn } from '../TrafficFilterList/columns'
import SearchBar from '../TrafficFilterList/SearchBar'

import RemoveButton from './RemoveButton'
import ApplyButton from './ApplyButton'

type TrafficFilterAssociationListProps = {
  items: NormalizedTrafficFilterInfo[]
  isLoading: boolean
  showSearch: boolean
  onClickApply?: (rulesetType: RulesetType) => void
} & (
  | {
      deployment: DeploymentGetResponse
      project?: never
    }
  | {
      project: AnyUserProject
      deployment?: never
    }
)

const TrafficFilterAssociationList: React.FC<TrafficFilterAssociationListProps> = ({
  items,
  isLoading,
  showSearch,
  onClickApply,
  deployment,
  project,
}) => {
  const actions: EuiTableActionsColumnType<NormalizedTrafficFilterInfo>['actions'] = [
    {
      render: ({ ruleset }: NormalizedTrafficFilterInfo) => (
        <Fragment>
          {deployment && <RemoveButton ruleset={ruleset} deployment={deployment} />}
          {project && <RemoveButton ruleset={ruleset} project={project} />}
        </Fragment>
      ),
    },
  ]

  const columns: Array<EuiBasicTableColumn<NormalizedTrafficFilterInfo> | any> = [
    nameColumn,
    typeColumn,
    providerColumn,
    regionColumn,
    {
      name: (
        <FormattedMessage
          id='deploymentNetworkSecurity.list.ruleset.actions.header'
          defaultMessage='Actions'
        />
      ),
      actions,
      width: '6rem',
    },
  ]

  return (
    <Fragment>
      {(showSearch || onClickApply) && (
        <Fragment>
          <EuiFlexGroup justifyContent='flexEnd'>
            {showSearch && (
              <EuiFlexItem grow={true}>
                <SearchBar />
              </EuiFlexItem>
            )}
            {onClickApply && (
              <EuiFlexItem grow={false}>
                <ApplyButton
                  onClick={onClickApply}
                  fill={true}
                  disablePrivateConnection={Boolean(project) || undefined}
                />
              </EuiFlexItem>
            )}
          </EuiFlexGroup>
          <EuiSpacer />
        </Fragment>
      )}

      <EuiInMemoryTable tableLayout='auto' items={items} columns={columns} loading={isLoading} />
    </Fragment>
  )
}

export default TrafficFilterAssociationList
